import { useForm } from '@tanstack/react-form'
import toNumber from 'lodash/toNumber'
import React from 'react'

import { ampli } from '@Analytics/ampli'
import { Drawer } from '@Components/Drawer'
import { DrawerContentBasic } from '@Components/Drawer/Content'
import { Button } from '@CoreComponents/Buttons/Button'
import { GiftCardCustomAmount } from '@Domains/Product/Features/GiftCard/CustomAmount'
import { GiftcardAmountOptions, useGiftCardAmountOptions } from '@Domains/Product/Features/GiftCard/Hooks'
import { GiftCardAmountOptionList } from '@Domains/Product/Features/GiftCard/Options'
import { useTranslation } from '@Globals/Translations'

import styles from './GiftCardAmount.module.scss'

/**
 * Prompt drawer for `AddToCartPromptGiftcard` amount codes: the add-to-cart call
 * came in without a (valid) gift card amount, so ask for one and resubmit.
 */
export const GiftCardAmountDrawer = ({
  defaultAmount,
  loading,
  onSubmit,
  open,
  ...props
}: React.ComponentProps<typeof Drawer> & {
  /** Amount from the original add-to-cart input, prefilled when it was out of bounds. */
  defaultAmount?: number
  loading: boolean
  onSubmit: (amount: number) => void
}) => {
  const { t } = useTranslation()
  const { amountOptions, maxValue, minValue } = useGiftCardAmountOptions()

  const [entered, setEntered] = React.useState(false)
  const [showError, setShowError] = React.useState(false)

  React.useEffect(() => {
    setEntered(true)
    ampli.promptOpen({ source_id: 'giftcard_amount_prompt' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isInRange = (value: string) => +value >= minValue && +value <= maxValue

  const form = useForm({
    defaultValues: {
      amount: defaultAmount
        ? (amountOptions.some(option => option.key === `${defaultAmount}`) ? `${defaultAmount}` : GiftcardAmountOptions.CUSTOM_AMOUNT) as string
        : GiftcardAmountOptions.SELECT_AMOUNT as string,
      customAmount: defaultAmount ? `${defaultAmount}` : '',
    },
    onSubmit: ({ value }) => {
      const amountNumber = value.amount === GiftcardAmountOptions.CUSTOM_AMOUNT ? +value.customAmount : toNumber(value.amount)

      if (!(amountNumber >= minValue && amountNumber <= maxValue)) {
        setShowError(true)
        return
      }

      setShowError(false)
      onSubmit(amountNumber)
    },
  })

  const customAmountValidator = ({ value }: { value: string }) => {
    if (isInRange(value)) {
      return undefined
    }

    return t('giftcard:GIFT_CARD_RANGE_ERROR', 'The amount must be between {{arg1}} and {{arg2}}', { arg1: minValue, arg2: maxValue })
  }

  return (
    <Drawer open={open ? entered : open} triggerRender={false} {...props}>
      <form
        className={styles.form}
        noValidate
        onSubmit={(e) => {
          e.preventDefault()
          form.handleSubmit().catch(() => {
            // Validation failures surface inline; swallow to avoid an unhandled rejection.
          })
        }}
      >
        <DrawerContentBasic
          footer={(
            <div className={styles.footer}>
              {showError && (
                <p className={styles.error}>{t('giftcard:GIFT_CARD_ERROR_NO_AMOUNT_HEADER')}</p>
              )}

              <Button fillWidth innerSpacing="medium" loading={loading} theme="black" type="submit">
                {t('cms:COF_SELECT')}
              </Button>
            </div>
          )}
          title={t('giftcard:GIFT_CARD_SELECT_AMOUNT')}
        >
          <form.Field name="amount">
            {amountField => (
              <GiftCardAmountOptionList field={amountField} options={amountOptions}>
                {amountField.state.value === GiftcardAmountOptions.CUSTOM_AMOUNT && (
                  <form.Field name="customAmount" validators={{ onChange: customAmountValidator }}>
                    {field => <GiftCardCustomAmount field={field} />}
                  </form.Field>
                )}
              </GiftCardAmountOptionList>
            )}
          </form.Field>
        </DrawerContentBasic>
      </form>
    </Drawer>
  )
}
